import { IndustryLayout } from "@/components/industry/IndustryLayout";
import { IndustryFAQ } from "@/components/industry/IndustryFAQ";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import { ArrowRight, Bot, Target, MessageSquare, BarChart3, Users, Workflow } from "lucide-react";
import { industryFAQs } from "@/data/industry-faqs";

const Technology = () => {
  return (
    <IndustryLayout industryName="Technology">
      {/* Hero Section */}
      <section 
        className="relative py-24 px-4 pt-40"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1518770660439-4636190af475')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 to-blue-900/90" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold text-white mb-6">
              Transform Your Tech Company with AI Agents
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Leverage our advanced AI agents to automate operations, enhance customer experiences, 
              and drive innovation in your technology business. Stay ahead of the competition with 
              intelligent automation and data-driven insights.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Get Started
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12">
            AI-Powered Solutions for Technology Companies
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Bot className="h-8 w-8 text-blue-600" />,
                title: "Intelligent Customer Support",
                description: "Deploy AI agents that provide 24/7 customer support, handle technical queries, and streamline user onboarding with automated assistance."
              },
              {
                icon: <Target className="h-8 w-8 text-blue-600" />,
                title: "AI-Powered Lead Generation",
                description: "Leverage intelligent agents to identify, qualify, and nurture leads automatically. Our AI agents analyze prospect behavior, personalize outreach, and optimize your sales pipeline."
              },
              {
                icon: <MessageSquare className="h-8 w-8 text-blue-600" />,
                title: "Smart Communication Hub",
                description: "AI agents that manage internal and external communications, automate responses, and ensure seamless collaboration across teams and channels."
              },
              {
                icon: <BarChart3 className="h-8 w-8 text-blue-600" />,
                title: "Intelligent Analytics",
                description: "Leverage AI agents to analyze user behavior, predict market trends, and deliver actionable insights for strategic decision-making."
              },
              {
                icon: <Users className="h-8 w-8 text-blue-600" />,
                title: "AI Team Collaboration",
                description: "Enhance team productivity with AI agents that facilitate project management, automate task assignments, and optimize resource allocation."
              },
              {
                icon: <Workflow className="h-8 w-8 text-blue-600" />,
                title: "Smart Workflow Automation",
                description: "Implement AI agents that optimize business processes, automate repetitive tasks, and enhance team productivity."
              }
            ].map((service, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="mb-4">{service.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <IndustryFAQ faqs={industryFAQs["technology"]} industryName="Technology" />

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Accelerate Your Growth?
          </h2>
          <p className="text-xl text-gray-200 mb-8">
            Let's discuss how we can help your technology company reach its full potential. 
            Schedule a free consultation with our tech industry experts.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100">
              Schedule Free Consultation
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </IndustryLayout>
  );
};

export default Technology;
